import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import type { ISpClaimedReward } from '@brushfire/types'
import { ConfirmationModalShell } from '@/shared/components/confirmation'
import { RiCloseLine } from '@/shared/icons/remix'
import { useSpClaimedRewardByTransactionId } from '../api'
import { RewardViewDetails } from './RewardViewDetails'
import {
  confirmRewardModalBackdropSx,
  confirmRewardModalCloseButtonSx,
  confirmRewardModalContentSx,
  confirmRewardModalHeaderSx,
  confirmRewardModalPaperSx,
  confirmRewardModalTitleSx,
} from '../styles/confirmRewardModalStyles'

interface Props {
  open: boolean
  reward: ISpClaimedReward | null
  onClose: () => void
}

export function ClaimedRewardViewModal({ open, reward, onClose }: Readonly<Props>) {
  const transactionId = open && reward ? reward.transactionId : undefined
  const { data: detail, isLoading, isError } = useSpClaimedRewardByTransactionId(transactionId)
  const displayReward = detail ?? reward

  if (!reward) return null

  return (
    <ConfirmationModalShell
      open={open}
      onClose={() => onClose()}
      dialogTestId="rewards-claimed-view-modal"
      ariaLabelledBy="rewards-claimed-view-title"
      paperSx={confirmRewardModalPaperSx}
      backdropSx={confirmRewardModalBackdropSx}
    >
      <Box sx={confirmRewardModalContentSx}>
        <Box sx={confirmRewardModalHeaderSx}>
          <Typography id="rewards-claimed-view-title" component="h2" sx={confirmRewardModalTitleSx}>
            Gift Card Details
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            data-testid="rewards-claimed-view-close"
            sx={confirmRewardModalCloseButtonSx}
          >
            <RiCloseLine size={24} aria-hidden />
          </IconButton>
        </Box>

        {isLoading && !detail ? (
          <>
            <Skeleton variant="rounded" height={72} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={24} width="50%" sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={140} />
          </>
        ) : null}

        {isError && !detail ? (
          <Typography sx={{ fontSize: '14px', color: '#DC2626', mb: 2 }}>
            Unable to load gift card details. Please try again.
          </Typography>
        ) : null}

        {displayReward ? <RewardViewDetails reward={displayReward} /> : null}
      </Box>
    </ConfirmationModalShell>
  )
}
